import { type MouseEvent, useRef } from "react";
import { Link } from "react-router";

import { Box, Card, Typography, useMediaQuery } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { Article, formatArticleDate } from "@/src/utils/blogUtils";

export interface ArticleCardProps {
  articleData: Article;
  onClick: (e: MouseEvent) => void;
  titleSize: "small" | "medium" | "large";
  to: string;
}

export const ArticleCard = ({
  articleData,
  titleSize,
  to,
  onClick
}: ArticleCardProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const contentRef = useRef<HTMLDivElement>(null);

  const titleStyles = {
    small: {
      fontSize: 16,
      fontWeight: theme.typography.fontWeightBold,
      fontStyle: "italic",
      lineHeight: 1.25
    },
    medium: {
      fontSize: 20,
      fontWeight: theme.typography.fontWeightBold,
      fontStyle: "italic",
      lineHeight: 1.25
    },
    large: {
      fontSize: 24,
      fontWeight: theme.typography.fontWeightBold,
      fontStyle: "italic",
      lineHeight: 1.25
    }
  };

  return (
    <Link to={to} prefetch="intent" style={{ textDecoration: "none" }}>
      <Card
        component="article"
        sx={{
          background: "none",
          boxShadow: "none",
          marginBottom: 2,
          maxWidth: isMobile ? "460px" : "100%",
          px: 2,
          mx: "auto",
          "*": {
            cursor: "pointer !important"
          }
        }}
        onClick={onClick}
      >
        <Box
          sx={{
            width: "100%",
            aspectRatio: "16 / 9",
            minHeight: "180px",
            marginBottom: 2,
            position: "relative"
          }}
        >
          <img
            src={articleData.mainImage.url}
            alt={articleData.mainImage.caption}
            width={512}
            height={288}
            loading="lazy"
            decoding="async"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: theme.shape.borderRadius
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.palette.text.disabled,
            marginTop: -1
          }}
        >
          {formatArticleDate(articleData.date)}
        </Typography>
        <Box
          ref={contentRef}
          sx={{
            maxHeight: isMobile ? "100%" : 200, // This can be adjusted based on your needs
            maskImage:
              contentRef.current &&
              contentRef.current.scrollHeight > 220 &&
              !isMobile
                ? `linear-gradient(${theme.palette.common.black} 80%, transparent)`
                : "none"
          }}
        >
          <Typography
            component="h2"
            variant="h2"
            sx={{
              "&.MuiTypography-root": { ...titleStyles[titleSize] },
              pb: 0,
              mb: 0.5
            }}
          >
            {articleData.title}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontWeight: theme.typography.fontWeightMedium
            }}
          >
            {articleData.ingress}
          </Typography>
        </Box>
      </Card>
    </Link>
  );
};
